// src/components/Footer.tsx
import { motion } from 'framer-motion';
import {
  SiFacebook,
  SiInstagram,
  SiX,
  SiGithub,
  SiWhatsapp,
} from '@icons-pack/react-simple-icons';
import { Mail, MapPin, ArrowUpRight } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

// WhatsApp Business number (Cloud API) - digits only, used to build the wa.me link
const WHATSAPP_NUMBER = '15553162280';
const WHATSAPP_DISPLAY = '+1 (555) 316-2280';
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}`;

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navigate = useNavigate();

  return (
    <footer className="bg-primary/90 text-zinc-300 pt-20 pb-8">
      <div className="container mx-auto px-4">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Column 1 - Logo and Description */}
          <div className="space-y-6">
            <Link
              to="/"
              onClick={(e) => {
                e.preventDefault();
                navigate('/');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}>
              <img
                src="/hoptira-logo-full-dark.svg"
                alt="Hoptira Logo"
                className="h-8 brightness-0 invert opacity-85 hover:opacity-100 transition-opacity cursor-pointer"
              />
            </Link>
            <p className="text-zinc-400 max-w-xs">
              We're a passionate team of digital creators, technologists, and
              problem solvers dedicated to simplifying complex challenges.
            </p>

            {/* Social Media Icons */}
            <div className="flex space-x-4">
              <a
                href="https://facebook.com/hoptira"
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-500 hover:text-zinc-200 transition-colors"
                aria-label="Facebook">
                <SiFacebook size={20} />
              </a>
              <a
                href="https://instagram.com/hoptiralabs"
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-500 hover:text-zinc-200 transition-colors"
                aria-label="Instagram">
                <SiInstagram size={20} />
              </a>
              <a
                href="https://twitter.com/hoptira"
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-500 hover:text-zinc-200 transition-colors"
                aria-label="Twitter">
                <SiX size={20} />
              </a>
              <a
                href="https://github.com/hoptira"
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-500 hover:text-zinc-200 transition-colors"
                aria-label="GitHub">
                <SiGithub size={20} />
              </a>
            </div>
          </div>

          {/* Column 2 - Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-4 text-lg">
              Quick Links
            </h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/"
                  className="text-zinc-400 hover:text-secondary transition-colors flex items-center gap-1 group w-fit">
                  Home
                  <ArrowUpRight
                    size={14}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                </Link>
              </li>
              <li>
                <Link
                  to="/about"
                  className="text-zinc-400 hover:text-secondary transition-colors flex items-center gap-1 group w-fit">
                  About Us
                  <ArrowUpRight
                    size={14}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                </Link>
              </li>
              {/* <li>
                <Link
                  to="/team"
                  className="text-zinc-400 hover:text-secondary transition-colors flex items-center gap-1 group w-fit">
                  Team
                  <ArrowUpRight
                    size={14}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                </Link>
              </li> */}
              <li>
                <Link
                  to="/services"
                  className="text-zinc-400 hover:text-secondary transition-colors flex items-center gap-1 group w-fit">
                  Services
                  <ArrowUpRight
                    size={14}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="text-zinc-400 hover:text-secondary transition-colors flex items-center gap-1 group w-fit">
                  Contact
                  <ArrowUpRight
                    size={14}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                </Link>
              </li>
              <li>
                <Link
                  to="/sitemap"
                  className="text-zinc-400 hover:text-secondary transition-colors flex items-center gap-1 group w-fit">
                  Sitemap
                  <ArrowUpRight
                    size={14}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 3 - Services */}
          <div>
            <h3 className="text-white font-semibold mb-4 text-lg">Services</h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/services?tab=web"
                  className="text-zinc-400 hover:text-secondary transition-colors flex items-center gap-1 group w-fit">
                  Web Development
                  <ArrowUpRight
                    size={14}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                </Link>
              </li>
              <li>
                <Link
                  to="/services?tab=app"
                  className="text-zinc-400 hover:text-secondary transition-colors flex items-center gap-1 group w-fit">
                  App Development
                  <ArrowUpRight
                    size={14}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                </Link>
              </li>
              <li>
                <Link
                  to="/services?tab=ai"
                  className="text-zinc-400 hover:text-secondary transition-colors flex items-center gap-1 group w-fit">
                  AI Solutions
                  <ArrowUpRight
                    size={14}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                </Link>
              </li>
              <li>
                <Link
                  to="/services?tab=whatsapp-business-solutions"
                  className="text-zinc-400 hover:text-secondary transition-colors flex items-center gap-1 group w-fit">
                  WhatsApp Business Solutions
                  <ArrowUpRight
                    size={14}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                </Link>
              </li>
              <li>
                <Link
                  to="/services?tab=analytics"
                  className="text-zinc-400 hover:text-secondary transition-colors flex items-center gap-1 group w-fit">
                  Analytics Implementations
                  <ArrowUpRight
                    size={14}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                </Link>
              </li>

              <li>
                <Link
                  to="/services?tab=automation"
                  className="text-zinc-400 hover:text-secondary transition-colors flex items-center gap-1 group w-fit">
                  Automations
                  <ArrowUpRight
                    size={14}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 4 - Contact Info */}
          <div>
            <h3 className="text-white font-semibold mb-4 text-lg">
              Contact Us
            </h3>
            <ul className="space-y-4">
              {/* WhatsApp */}
              <li className="flex items-start gap-3">
                <SiWhatsapp
                  size={20}
                  className="text-zinc-500 mt-1 flex-shrink-0"
                />
                <a
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-zinc-400 hover:text-secondary transition-colors">
                  {WHATSAPP_DISPLAY}
                </a>
              </li>
              {/* Email */}
              <li className="flex items-start gap-3">
                <Mail size={20} className="text-zinc-500 mt-1 flex-shrink-0" />
                <a
                  href="mailto:hello@hoptira.com"
                  className="text-zinc-400 hover:text-secondary transition-colors">
                  hello@hoptira.com
                </a>
              </li>
              {/* Address */}
              <li className="flex items-start gap-3">
                <MapPin
                  size={20}
                  className="text-zinc-500 mt-1 flex-shrink-0"
                />
                <address className="text-zinc-400 not-italic">
                  Hoptira Labs LLC
                  <br />
                  30 N Gould St Ste R,
                  <br />
                  Sheridan, WY 82801
                </address>
              </li>
            </ul>
          </div>
        </div>

        {/* Back to Top Button */}
        <div className="flex justify-center mb-8">
          <motion.button
            onClick={scrollToTop}
            className="bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white rounded-full p-3 transition-colors"
            whileHover={{ y: -3 }}
            whileTap={{ scale: 0.9 }}
            aria-label="Back to top">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round">
              <path d="m18 15-6-6-6 6" />
            </svg>
          </motion.button>
        </div>

        {/* Divider */}
        <div className="h-px bg-zinc-500 mb-8"></div>

        {/* Bottom Bar with Copyright */}
        <div className="flex flex-col md:flex-row justify-between items-center text-sm text-zinc-500">
          <div className="mb-4 md:mb-0">
            &copy; {currentYear} Hoptira Labs LLC. All rights reserved.
          </div>
          <div className="flex space-x-6">
            <a
              href="/privacy-policy"
              className="hover:text-zinc-300 transition-colors">
              Privacy
            </a>
            <a href="/terms" className="hover:text-zinc-300 transition-colors">
              Terms
            </a>
            <a
              href="/sitemap"
              className="hover:text-zinc-300 transition-colors">
              Sitemap
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
